import { Fragment, useEffect, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
    ArrowLeftIcon,
    BellAlertIcon,
    CalendarDaysIcon,
    ClockIcon,
    DocumentTextIcon,
    EnvelopeIcon,
    ExclamationTriangleIcon,
    PaperAirplaneIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { SECTION_PERMISSIONS } from "@/config/permissions";
import {
    buildApprovalSubmissionFeedback,
    getApprovalQueuePath,
} from "@/features/approvals/approval-utils";
import type { ApprovalSubmission } from "@/features/approvals/types";
import { usePermissions } from "@/features/auth/usePermissions";
import { extractApiError } from "@/lib/api-error";
import type { ApiResponse } from "@/types/api";
import {
    getAnnouncementEmailCount,
    getAnnouncementFailedCount,
    getAnnouncementId,
    getAnnouncementPushCount,
} from "../announcement-utils";
import {
    createAndSendAnnouncement,
    createDraftAnnouncement,
    scheduleAnnouncement,
} from "../api/messagingApi";
import {
    ADMIN_ANNOUNCEMENT_EMAIL_TEMPLATES,
    ADMIN_ANNOUNCEMENT_EMAIL_TEMPLATE_OPTIONS,
    createInitialAnnouncementTemplateDrafts,
    DEFAULT_ADMIN_ANNOUNCEMENT_EMAIL_TEMPLATE,
} from "../templates";
import {
    renderAnnouncementEmailHtmlDocument,
    renderAnnouncementEmailMarkup,
} from "../templates/renderEmailHtml";
import type {
    AdminAnnouncementEmailTemplateKey,
} from "../templates";
import type {
    AnnouncementType,
    AnnouncementMutationResult,
    CreateAnnouncementRequest,
    ScheduleAnnouncementRequest,
    TargetAudience,
} from "../types";

type AnnouncementComposerMode = "send" | "draft" | "schedule";

type FeedbackState = {
    type: "error" | "info";
    text: string;
};

type AnnouncementImageSource = "none" | "url" | "upload";

type FormErrors = {
    title?: string;
    message?: string;
    targetUserIds?: string;
    channels?: string;
    scheduledFor?: string;
    image?: string;
    emailTemplateFields?: Record<string, string>;
};

const ANNOUNCEMENT_TYPES: { value: AnnouncementType; label: string }[] = [
    { value: "ROAD_CLOSURE", label: "Road Closure" },
    { value: "SYSTEM_MAINTENANCE", label: "System Maintenance" },
    { value: "SAFETY_WARNING", label: "Safety Warning" },
    { value: "PROMOTION", label: "Promotion" },
    { value: "GENERAL", label: "General" },
    { value: "EMERGENCY", label: "Emergency" },
];

const TARGET_AUDIENCES: { value: TargetAudience; label: string }[] = [
    { value: "ALL", label: "All Users" },
    { value: "RIDERS", label: "Riders Only" },
    { value: "DRIVERS", label: "Drivers Only" },
    { value: "SPECIFIC_USERS", label: "Specific Users" },
];

const DEFAULT_EMAIL_TEMPLATE_BY_ANNOUNCEMENT_TYPE: Record<
    AnnouncementType,
    AdminAnnouncementEmailTemplateKey
> = {
    ROAD_CLOSURE: "SERVICE_UPDATE",
    SYSTEM_MAINTENANCE: "SERVICE_UPDATE",
    SAFETY_WARNING: "SAFETY_ALERT",
    PROMOTION: "PROMOTION",
    GENERAL: "GENERAL_ANNOUNCEMENT",
    EMERGENCY: "SAFETY_ALERT",
};

const MODE_OPTIONS: Array<{
    value: AnnouncementComposerMode;
    label: string;
    description: string;
}> = [
    {
        value: "send",
        label: "Send Now",
        description: "Prepare an immediate send request for super admin approval.",
    },
    {
        value: "schedule",
        label: "Schedule",
        description: "Prepare a scheduled delivery request for super admin approval.",
    },
    {
        value: "draft",
        label: "Draft",
        description: "Save work without sending anything yet.",
    },
];

const fmtLabel = (value: string | null | undefined) => {
    if (!value) return "—";
    return value
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const fmtDateTime = (value: string) => {
    if (!value) return "—";

    return new Date(value).toLocaleString("en-NG", {
        dateStyle: "medium",
        timeStyle: "short",
    });
};

const getModeFromSearch = (searchMode: string | null): AnnouncementComposerMode => {
    if (
        searchMode === "draft" ||
        searchMode === "schedule" ||
        searchMode === "send"
    ) {
        return searchMode;
    }

    return "send";
};

const toApiDateTime = (value: string) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const buildPushPreviewExcerpt = (value: string) => {
    const normalized = value.replace(/\s+/g, " ").trim();

    if (!normalized) {
        return "Your message preview will appear here as the admin types.";
    }

    if (normalized.length <= 160) {
        return normalized;
    }

    return `${normalized.slice(0, 157).trimEnd()}...`;
};

const isHttpsUrl = (value: string) => {
    try {
        const url = new URL(value);
        return url.protocol === "https:";
    } catch {
        return false;
    }
};

const getDefaultEmailTemplateForAnnouncementType = (
    type: AnnouncementType
): AdminAnnouncementEmailTemplateKey =>
    DEFAULT_EMAIL_TEMPLATE_BY_ANNOUNCEMENT_TYPE[type] ??
    DEFAULT_ADMIN_ANNOUNCEMENT_EMAIL_TEMPLATE;

const appendFormDataValue = (
    formData: FormData,
    key: string,
    value: string | boolean | null | undefined
) => {
    if (value === undefined || value === null || value === "") {
        return;
    }

    formData.append(key, typeof value === "boolean" ? String(value) : value);
};

const toAnnouncementFormData = (
    payload: CreateAnnouncementRequest | ScheduleAnnouncementRequest,
    imageFile: File
) => {
    const formData = new FormData();

    appendFormDataValue(formData, "title", payload.title);
    appendFormDataValue(formData, "message", payload.message);
    appendFormDataValue(formData, "announcementType", payload.announcementType);
    appendFormDataValue(formData, "targetAudience", payload.targetAudience);
    appendFormDataValue(formData, "targetUserIds", payload.targetUserIds);
    appendFormDataValue(formData, "targetRegion", payload.targetRegion);
    appendFormDataValue(formData, "imageUrl", payload.imageUrl);
    appendFormDataValue(formData, "sendPush", payload.sendPush);
    appendFormDataValue(formData, "sendSms", payload.sendSms);
    appendFormDataValue(formData, "sendEmail", payload.sendEmail);
    appendFormDataValue(formData, "emailHtml", payload.emailHtml);
    appendFormDataValue(formData, "emailText", payload.emailText);
    appendFormDataValue(formData, "expiresAt", payload.expiresAt);

    if ("scheduledFor" in payload) {
        appendFormDataValue(formData, "scheduledFor", payload.scheduledFor);
    }

    formData.append("imageFile", imageFile);

    return formData;
};

const buildAnnouncementPayload = ({
    title,
    message,
    announcementType,
    targetAudience,
    imageUrl,
    sendPush,
    sendEmail,
    targetUserIds,
    emailHtml,
    emailText,
}: {
    title: string;
    message: string;
    announcementType: AnnouncementType;
    targetAudience: TargetAudience;
    imageUrl?: string | null;
    sendPush: boolean;
    sendEmail: boolean;
    targetUserIds: string[];
    emailHtml?: string | null;
    emailText?: string | null;
}): CreateAnnouncementRequest => ({
    title: title.trim(),
    message: message.trim(),
    announcementType,
    targetAudience,
    targetUserIds:
        targetAudience === "SPECIFIC_USERS"
            ? JSON.stringify(targetUserIds)
            : null,
    imageUrl: imageUrl?.trim() || null,
    sendPush,
    sendSms: false,
    sendEmail,
    emailHtml: emailHtml?.trim() || null,
    emailText: emailText?.trim() || null,
});

const buildMutationFeedback = (
    responseMessage: string | undefined,
    fallbackMessage: string,
    data: AnnouncementMutationResult
) => {
    const counts: string[] = [];
    const pushCount = getAnnouncementPushCount(data);
    const emailCount = getAnnouncementEmailCount(data);
    const failedCount = getAnnouncementFailedCount(data);

    if (pushCount && pushCount > 0) {
        counts.push(`${pushCount} push sent`);
    }

    if (emailCount && emailCount > 0) {
        counts.push(`${emailCount} email sent`);
    }

    if (failedCount && failedCount > 0) {
        counts.push(`${failedCount} failed`);
    }

    const baseMessage = responseMessage || fallbackMessage;
    return counts.length > 0 ? `${baseMessage} (${counts.join(", ")})` : baseMessage;
};

const getConfirmationCopy = (
    mode: AnnouncementComposerMode,
    scheduledFor: string
) => {
    if (mode === "send") {
        return {
            title: "Submit send request for approval?",
            description:
                "This will submit the announcement to the super admin approval queue. Delivery will only begin after approval.",
            cta: "Submit for Approval",
        };
    }

    if (mode === "schedule") {
        return {
            title: "Submit schedule request for approval?",
            description: `This will submit the schedule request for ${fmtDateTime(
                scheduledFor
            )}. The announcement will only be created and scheduled after approval.`,
            cta: "Submit Schedule Request",
        };
    }

    return {
        title: "Save this draft?",
        description:
            "This will create a draft announcement that stays unpublished until you send it later.",
        cta: "Save Draft",
    };
};

export const AnnouncementComposerRoute = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { hasPermission } = usePermissions();
    const [searchParams, setSearchParams] = useSearchParams();
    const mode = getModeFromSearch(searchParams.get("mode"));

    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");
    const [announcementType, setAnnouncementType] =
        useState<AnnouncementType>("GENERAL");
    const [emailTemplate, setEmailTemplate] =
        useState<AdminAnnouncementEmailTemplateKey>(
            DEFAULT_ADMIN_ANNOUNCEMENT_EMAIL_TEMPLATE
        );
    const [emailTemplateDrafts, setEmailTemplateDrafts] = useState(() =>
        createInitialAnnouncementTemplateDrafts()
    );
    const [targetAudience, setTargetAudience] = useState<TargetAudience>("ALL");
    const [imageSource, setImageSource] = useState<AnnouncementImageSource>("none");
    const [imageUrl, setImageUrl] = useState("");
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [sendPush, setSendPush] = useState(true);
    const [sendEmail, setSendEmail] = useState(false);
    const [targetUserIdsInput, setTargetUserIdsInput] = useState("");
    const [scheduledFor, setScheduledFor] = useState("");
    const [feedback, setFeedback] = useState<FeedbackState | null>(null);
    const [errors, setErrors] = useState<FormErrors>({});
    const [confirmOpen, setConfirmOpen] = useState(false);

    const targetUserIds = useMemo(
        () =>
            targetUserIdsInput
                .split(",")
                .map((id) => id.trim())
                .filter(Boolean),
        [targetUserIdsInput]
    );

    const uploadedImagePreviewUrl = useMemo(
        () => (imageFile ? URL.createObjectURL(imageFile) : null),
        [imageFile]
    );

    useEffect(
        () => () => {
            if (uploadedImagePreviewUrl) {
                URL.revokeObjectURL(uploadedImagePreviewUrl);
            }
        },
        [uploadedImagePreviewUrl]
    );

    const selectedEmailTemplate =
        ADMIN_ANNOUNCEMENT_EMAIL_TEMPLATES[emailTemplate];
    const selectedEmailTemplateValues = emailTemplateDrafts[emailTemplate] ?? {};
    const emailTextFallback = selectedEmailTemplate.buildBody({
        title,
        message,
        targetAudience,
        targetRegion: "",
        expiresAt: "",
        fields: selectedEmailTemplateValues,
    });
    const selectedPublicImageUrl =
        imageSource === "url" ? imageUrl.trim() || null : null;
    const previewImageUrl =
        imageSource === "upload"
            ? uploadedImagePreviewUrl
            : selectedPublicImageUrl;
    const emailHtmlMarkup = renderAnnouncementEmailMarkup({
        templateKey: emailTemplate,
        title: title.trim() || "Ride2Five Update",
        message,
        targetAudience,
        targetRegion: "",
        expiresAt: "",
        fields: selectedEmailTemplateValues,
        imageUrl: previewImageUrl,
    });
    const emailHtmlDocument = sendEmail
        ? renderAnnouncementEmailHtmlDocument({
              templateKey: emailTemplate,
              title: title.trim() || "Ride2Five Update",
              message,
              targetAudience,
              targetRegion: "",
              expiresAt: "",
              fields: selectedEmailTemplateValues,
              imageUrl: selectedPublicImageUrl,
          })
        : null;
    const pushMessage = message.trim();

    const basePayload = useMemo(
        () =>
            buildAnnouncementPayload({
                title,
                message: pushMessage,
                announcementType,
                targetAudience,
                imageUrl: selectedPublicImageUrl,
                sendPush,
                sendEmail,
                targetUserIds,
                emailHtml: sendEmail ? emailHtmlDocument : null,
                emailText: sendEmail ? emailTextFallback : null,
            }),
        [
            title,
            pushMessage,
            announcementType,
            targetAudience,
            selectedPublicImageUrl,
            sendPush,
            sendEmail,
            targetUserIds,
            emailHtmlDocument,
            emailTextFallback,
        ]
    );

    const canViewApprovals = hasPermission(SECTION_PERMISSIONS.APPROVALS[0]);

    const handleDraftSuccess = (
        response: { message: string; data: AnnouncementMutationResult },
        fallbackMessage: string
    ) => {
        queryClient.invalidateQueries({ queryKey: ["announcements"] });
        const announcementId = getAnnouncementId(response.data);
        const feedbackText = buildMutationFeedback(
            response.message,
            fallbackMessage,
            response.data
        );

        if (!announcementId) {
            navigate("/notifications/announcements", {
                state: {
                    feedback: {
                        type: "success",
                        text: `${feedbackText} The backend did not return an announcement ID, so the details page could not be opened.`,
                    },
                },
            });
            return;
        }

        navigate(`/notifications/announcements/${announcementId}`, {
            state: {
                feedback: {
                    type: "success",
                    text: feedbackText,
                },
            },
        });
    };

    const handleApprovalSuccess = (
        response: ApiResponse<ApprovalSubmission>,
        fallbackMessage: string
    ) => {
        queryClient.invalidateQueries({ queryKey: ["announcements"] });
        const feedbackText = buildApprovalSubmissionFeedback(response, fallbackMessage);

        if (canViewApprovals && response.data.approvalId) {
            navigate(getApprovalQueuePath(response.data.approvalId), {
                state: {
                    feedback: {
                        type: "success",
                        text: feedbackText,
                    },
                },
            });
            return;
        }

        navigate("/notifications/announcements", {
            state: {
                feedback: {
                    type: "success",
                    text: feedbackText,
                },
            },
        });
    };

    const sendPayload =
        imageSource === "upload" && imageFile
            ? toAnnouncementFormData(basePayload, imageFile)
            : basePayload;

    const scheduledPayload: ScheduleAnnouncementRequest = {
        ...basePayload,
        scheduledFor: toApiDateTime(scheduledFor) ?? scheduledFor,
    };

    const schedulePayload =
        imageSource === "upload" && imageFile
            ? toAnnouncementFormData(scheduledPayload, imageFile)
            : scheduledPayload;

    const sendMutation = useMutation({
        mutationFn: () => createAndSendAnnouncement(sendPayload),
        onSuccess: (response) =>
            handleApprovalSuccess(response, "Announcement submitted for approval."),
        onError: (error) =>
            setFeedback({ type: "error", text: extractApiError(error) }),
    });

    const draftMutation = useMutation({
        mutationFn: () => createDraftAnnouncement(sendPayload),
        onSuccess: (response) => handleDraftSuccess(response, "Draft created."),
        onError: (error) =>
            setFeedback({ type: "error", text: extractApiError(error) }),
    });

    const scheduleMutation = useMutation({
        mutationFn: () => scheduleAnnouncement(schedulePayload),
        onSuccess: (response) =>
            handleApprovalSuccess(response, "Schedule request submitted for approval."),
        onError: (error) =>
            setFeedback({ type: "error", text: extractApiError(error) }),
    });

    const isSubmitting =
        sendMutation.isPending ||
        draftMutation.isPending ||
        scheduleMutation.isPending;

    const confirmationCopy = getConfirmationCopy(mode, scheduledFor);
    const selectedMode = MODE_OPTIONS.find((option) => option.value === mode);
    const deliveryChannels = [
        sendPush ? "Push" : null,
        sendEmail ? "Email" : null,
    ].filter(Boolean) as string[];
    const titlePreview = title.trim() || "Announcement title";
    const messagePreview = buildPushPreviewExcerpt(pushMessage);
    const imageSourceLabel =
        imageSource === "url"
            ? "Remote URL"
            : imageSource === "upload"
              ? "Uploaded File"
              : "None";

    const updateEmailTemplate = (
        nextTemplate: AdminAnnouncementEmailTemplateKey
    ) => {
        setEmailTemplate(nextTemplate);
        setAnnouncementType(
            ADMIN_ANNOUNCEMENT_EMAIL_TEMPLATES[nextTemplate]
                .recommendedAnnouncementType
        );
        setErrors((previousErrors) => ({
            ...previousErrors,
            emailTemplateFields: undefined,
        }));
    };

    const updateAnnouncementType = (nextType: AnnouncementType) => {
        setAnnouncementType(nextType);
        setEmailTemplate(getDefaultEmailTemplateForAnnouncementType(nextType));
        setErrors((previousErrors) => ({
            ...previousErrors,
            emailTemplateFields: undefined,
        }));
    };

    const updateTemplateField = (fieldId: string, value: string) => {
        setEmailTemplateDrafts((previousDrafts) => ({
            ...previousDrafts,
            [emailTemplate]: {
                ...(previousDrafts[emailTemplate] ?? {}),
                [fieldId]: value,
            },
        }));
    };

    const clearTemplateFieldError = (fieldId: string) => {
        setErrors((previousErrors) => {
            if (!previousErrors.emailTemplateFields?.[fieldId]) {
                return previousErrors;
            }

            const nextTemplateFieldErrors = {
                ...previousErrors.emailTemplateFields,
            };
            delete nextTemplateFieldErrors[fieldId];

            return {
                ...previousErrors,
                emailTemplateFields:
                    Object.keys(nextTemplateFieldErrors).length > 0
                        ? nextTemplateFieldErrors
                        : undefined,
            };
        });
    };

    const updateImageSelection = (nextSource: AnnouncementImageSource) => {
        setImageSource(nextSource);
        clearFieldError("image");

        if (nextSource !== "url") {
            setImageUrl("");
        }

        if (nextSource !== "upload") {
            setImageFile(null);
        }
    };

    const validateForm = () => {
        const nextErrors: FormErrors = {};

        if (!title.trim()) {
            nextErrors.title = "Title is required.";
        }

        if (!message.trim()) {
            nextErrors.message = "Message is required.";
        }

        if (!sendPush && !sendEmail) {
            nextErrors.channels = "Select at least one delivery channel.";
        }

        if (targetAudience === "SPECIFIC_USERS" && targetUserIds.length === 0) {
            nextErrors.targetUserIds =
                "Provide at least one user ID when targeting specific users.";
        }

        if (mode === "schedule" && !scheduledFor) {
            nextErrors.scheduledFor = "Choose when this announcement should go out.";
        }

        if (imageSource === "url") {
            if (!imageUrl.trim()) {
                nextErrors.image = "Provide an HTTPS image URL or switch the image source.";
            } else if (!isHttpsUrl(imageUrl.trim())) {
                nextErrors.image = "Image URLs must use HTTPS.";
            }
        }

        if (imageSource === "upload" && !imageFile) {
            nextErrors.image = "Choose an image file to upload or switch the image source.";
        }

        if (sendEmail) {
            const templateFieldErrors = selectedEmailTemplate.fields.reduce(
                (fieldErrors, field) => {
                    if (
                        field.required &&
                        !selectedEmailTemplateValues[field.id]?.trim()
                    ) {
                        return {
                            ...fieldErrors,
                            [field.id]: `${field.label} is required.`,
                        };
                    }

                    return fieldErrors;
                },
                {} as Record<string, string>
            );

            if (Object.keys(templateFieldErrors).length > 0) {
                nextErrors.emailTemplateFields = templateFieldErrors;
            }
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handlePrimaryAction = () => {
        setFeedback(null);

        if (!validateForm()) {
            return;
        }

        setConfirmOpen(true);
    };

    const handleConfirmSubmit = () => {
        if (mode === "send") {
            sendMutation.mutate();
        } else if (mode === "schedule") {
            scheduleMutation.mutate();
        } else {
            draftMutation.mutate();
        }
    };

    const updateMode = (nextMode: AnnouncementComposerMode) => {
        setSearchParams({ mode: nextMode });
        setFeedback(null);
        setErrors({});
    };

    const clearFieldError = (field: keyof FormErrors) => {
        setErrors((previousErrors) => {
            const nextErrors = { ...previousErrors };
            delete nextErrors[field];
            return nextErrors;
        });
    };

    return (
        <div className="space-y-6">
            <div className="space-y-3">
                <Link
                    to="/notifications/announcements"
                    className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:underline dark:text-primary-400"
                >
                    <ArrowLeftIcon className="h-4 w-4" /> Back to Announcements
                </Link>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                            Compose Announcement
                        </h2>
                        <p className="mt-1 max-w-3xl text-sm text-slate-500 dark:text-slate-400">
                            Create a new announcement on its own page, preview the
                            delivery experience before sending, and explicitly
                            confirm the action before anything is saved or
                            dispatched.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {MODE_OPTIONS.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => updateMode(option.value)}
                                className={`rounded-xl border px-4 py-2 text-sm font-medium transition ${
                                    mode === option.value
                                        ? "border-primary-300 bg-primary-50 text-primary-700 dark:border-primary-700 dark:bg-primary-900/20 dark:text-primary-300"
                                        : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                                }`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {feedback ? (
                <div
                    className={`rounded-2xl border px-4 py-3 text-sm ${
                        feedback.type === "error"
                            ? "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300"
                            : "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300"
                    }`}
                >
                    {feedback.text}
                </div>
            ) : null}

            <div className="grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)]">
                <section className="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                            {selectedMode?.label}
                        </h3>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                            {selectedMode?.description}
                        </p>
                    </div>

                    <div className="space-y-5 p-6">
                        <div>
                            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                                Title *
                            </label>
                            <input
                                type="text"
                                value={title}
                                onChange={(event) => {
                                    setTitle(event.target.value);
                                    clearFieldError("title");
                                }}
                                placeholder="Announcement title"
                                className={`w-full rounded-xl border px-4 py-3 text-sm text-slate-900 transition focus:outline-none focus:ring-4 dark:bg-slate-900 dark:text-slate-100 ${
                                    errors.title
                                        ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500/15 dark:border-rose-700"
                                        : "border-slate-200 focus:border-primary-500 focus:ring-primary-500/15 dark:border-slate-700"
                                }`}
                            />
                            {errors.title ? (
                                <p className="mt-1.5 text-sm text-rose-600 dark:text-rose-400">
                                    {errors.title}
                                </p>
                            ) : null}
                        </div>

                        <div>
                            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                                Email Template
                            </label>
                            <select
                                value={emailTemplate}
                                onChange={(event) =>
                                    updateEmailTemplate(
                                        event.target
                                            .value as AdminAnnouncementEmailTemplateKey
                                    )
                                }
                                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                            >
                                {ADMIN_ANNOUNCEMENT_EMAIL_TEMPLATE_OPTIONS.map(
                                    (templateOption) => (
                                        <option
                                            key={templateOption.value}
                                            value={templateOption.value}
                                        >
                                            {templateOption.label}
                                        </option>
                                    )
                                )}
                            </select>
                            <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                                {selectedEmailTemplate.description}
                            </p>
                            {!sendEmail ? (
                                <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                                    This template becomes active when the Email
                                    delivery channel is enabled.
                                </p>
                            ) : null}
                        </div>

                        <div>
                            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                                {sendEmail
                                    ? selectedEmailTemplate.messageLabel
                                    : "Message *"}
                            </label>
                            <textarea
                                value={message}
                                onChange={(event) => {
                                    setMessage(event.target.value);
                                    clearFieldError("message");
                                }}
                                rows={6}
                                placeholder={
                                    sendEmail
                                        ? selectedEmailTemplate.messagePlaceholder
                                        : "Write the announcement body..."
                                }
                                className={`w-full rounded-xl border px-4 py-3 text-sm text-slate-900 transition focus:outline-none focus:ring-4 dark:bg-slate-900 dark:text-slate-100 ${
                                    errors.message
                                        ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500/15 dark:border-rose-700"
                                        : "border-slate-200 focus:border-primary-500 focus:ring-primary-500/15 dark:border-slate-700"
                                }`}
                            />
                            {errors.message ? (
                                <p className="mt-1.5 text-sm text-rose-600 dark:text-rose-400">
                                    {errors.message}
                                </p>
                            ) : null}
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Type
                                </label>
                                <select
                                    value={announcementType}
                                    onChange={(event) =>
                                        updateAnnouncementType(
                                            event.target.value as AnnouncementType
                                        )
                                    }
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                >
                                    {ANNOUNCEMENT_TYPES.map((typeOption) => (
                                        <option
                                            key={typeOption.value}
                                            value={typeOption.value}
                                        >
                                            {typeOption.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Target Audience
                                </label>
                                <select
                                    value={targetAudience}
                                    onChange={(event) => {
                                        setTargetAudience(
                                            event.target.value as TargetAudience
                                        );
                                        clearFieldError("targetUserIds");
                                    }}
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                >
                                    {TARGET_AUDIENCES.map((audienceOption) => (
                                        <option
                                            key={audienceOption.value}
                                            value={audienceOption.value}
                                        >
                                            {audienceOption.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-800/40">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div>
                                    <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                        Announcement Image
                                    </h4>
                                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                        Optional image for push notifications and custom email content.
                                    </p>
                                </div>
                                <span className="rounded-full bg-white px-3 py-1 text-[11px] font-medium text-slate-600 dark:bg-slate-900 dark:text-slate-300">
                                    Source: {imageSourceLabel}
                                </span>
                            </div>

                            <div className="mt-4 flex flex-wrap gap-2">
                                {[
                                    { value: "none", label: "No Image" },
                                    { value: "url", label: "Image URL" },
                                    { value: "upload", label: "Upload File" },
                                ].map((option) => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        onClick={() =>
                                            updateImageSelection(
                                                option.value as AnnouncementImageSource
                                            )
                                        }
                                        className={`rounded-xl border px-4 py-2 text-sm font-medium transition ${
                                            imageSource === option.value
                                                ? "border-primary-300 bg-primary-50 text-primary-700 dark:border-primary-700 dark:bg-primary-900/20 dark:text-primary-300"
                                                : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                                        }`}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>

                            {imageSource === "url" ? (
                                <div className="mt-4">
                                    <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                                        Image URL
                                    </label>
                                    <input
                                        type="url"
                                        value={imageUrl}
                                        onChange={(event) => {
                                            setImageUrl(event.target.value);
                                            clearFieldError("image");
                                        }}
                                        placeholder="https://example.com/announcement-banner.png"
                                        className={`w-full rounded-xl border px-4 py-3 text-sm text-slate-900 transition focus:outline-none focus:ring-4 dark:bg-slate-900 dark:text-slate-100 ${
                                            errors.image
                                                ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500/15 dark:border-rose-700"
                                                : "border-slate-200 focus:border-primary-500 focus:ring-primary-500/15 dark:border-slate-700"
                                        }`}
                                    />
                                    <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                                        Use an HTTPS image URL when you want the image embedded in the custom email HTML.
                                    </p>
                                </div>
                            ) : null}

                            {imageSource === "upload" ? (
                                <div className="mt-4">
                                    <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                                        Upload Image File
                                    </label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(event) => {
                                            setImageFile(event.target.files?.[0] ?? null);
                                            clearFieldError("image");
                                        }}
                                        className={`block w-full rounded-xl border px-4 py-3 text-sm text-slate-900 transition file:mr-4 file:rounded-lg file:border-0 file:bg-primary-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary-700 focus:outline-none focus:ring-4 dark:bg-slate-900 dark:text-slate-100 dark:file:bg-primary-900/20 dark:file:text-primary-300 ${
                                            errors.image
                                                ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500/15 dark:border-rose-700"
                                                : "border-slate-200 focus:border-primary-500 focus:ring-primary-500/15 dark:border-slate-700"
                                        }`}
                                    />
                                    <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                                        Uploaded files are saved on the announcement record. For custom email HTML, use an HTTPS image URL if you need the image embedded directly in the email.
                                    </p>
                                </div>
                            ) : null}

                            {previewImageUrl ? (
                                <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
                                    <img
                                        src={previewImageUrl}
                                        alt="Announcement preview"
                                        className="max-h-48 w-full rounded-xl object-cover"
                                    />
                                </div>
                            ) : null}

                            {errors.image ? (
                                <p className="mt-3 text-sm text-rose-600 dark:text-rose-400">
                                    {errors.image}
                                </p>
                            ) : null}
                        </div>

                        {sendEmail ? (
                            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-800/40">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div>
                                        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                            {selectedEmailTemplate.label} Fields
                                        </h4>
                                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                            These fields shape the email structure and the generated delivery body.
                                        </p>
                                    </div>
                                    <span
                                        className={`rounded-full px-3 py-1 text-[11px] font-medium ${selectedEmailTemplate.previewBadgeClassName}`}
                                    >
                                        Recommended:{" "}
                                        {fmtLabel(
                                            selectedEmailTemplate.recommendedAnnouncementType
                                        )}
                                    </span>
                                </div>

                                <div className="mt-4 grid gap-4 md:grid-cols-2">
                                    {selectedEmailTemplate.fields.map((field) => (
                                        <div
                                            key={field.id}
                                            className={
                                                field.type === "textarea"
                                                    ? "md:col-span-2"
                                                    : undefined
                                            }
                                        >
                                            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                                                {field.label}
                                                {field.required ? " *" : ""}
                                            </label>
                                            {field.type === "textarea" ? (
                                                <textarea
                                                    value={
                                                        selectedEmailTemplateValues[
                                                            field.id
                                                        ] ?? ""
                                                    }
                                                    onChange={(event) => {
                                                        updateTemplateField(
                                                            field.id,
                                                            event.target.value
                                                        );
                                                        clearTemplateFieldError(
                                                            field.id
                                                        );
                                                    }}
                                                    rows={field.rows ?? 4}
                                                    placeholder={field.placeholder}
                                                    className={`w-full rounded-xl border px-4 py-3 text-sm text-slate-900 transition focus:outline-none focus:ring-4 dark:bg-slate-900 dark:text-slate-100 ${
                                                        errors.emailTemplateFields?.[
                                                            field.id
                                                        ]
                                                            ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500/15 dark:border-rose-700"
                                                            : "border-slate-200 focus:border-primary-500 focus:ring-primary-500/15 dark:border-slate-700"
                                                    }`}
                                                />
                                            ) : (
                                                <input
                                                    type="text"
                                                    value={
                                                        selectedEmailTemplateValues[
                                                            field.id
                                                        ] ?? ""
                                                    }
                                                    onChange={(event) => {
                                                        updateTemplateField(
                                                            field.id,
                                                            event.target.value
                                                        );
                                                        clearTemplateFieldError(
                                                            field.id
                                                        );
                                                    }}
                                                    placeholder={field.placeholder}
                                                    className={`w-full rounded-xl border px-4 py-3 text-sm text-slate-900 transition focus:outline-none focus:ring-4 dark:bg-slate-900 dark:text-slate-100 ${
                                                        errors.emailTemplateFields?.[
                                                            field.id
                                                        ]
                                                            ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500/15 dark:border-rose-700"
                                                            : "border-slate-200 focus:border-primary-500 focus:ring-primary-500/15 dark:border-slate-700"
                                                    }`}
                                                />
                                            )}
                                            {field.description ? (
                                                <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                                                    {field.description}
                                                </p>
                                            ) : null}
                                            {errors.emailTemplateFields?.[field.id] ? (
                                                <p className="mt-1.5 text-sm text-rose-600 dark:text-rose-400">
                                                    {
                                                        errors.emailTemplateFields[
                                                            field.id
                                                        ]
                                                    }
                                                </p>
                                            ) : null}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : null}

                        {targetAudience === "SPECIFIC_USERS" ? (
                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                                    User IDs *
                                </label>
                                <textarea
                                    value={targetUserIdsInput}
                                    onChange={(event) => {
                                        setTargetUserIdsInput(event.target.value);
                                        clearFieldError("targetUserIds");
                                    }}
                                    rows={3}
                                    placeholder="uuid-1, uuid-2, uuid-3"
                                    className={`w-full rounded-xl border px-4 py-3 text-sm text-slate-900 transition focus:outline-none focus:ring-4 dark:bg-slate-900 dark:text-slate-100 ${
                                        errors.targetUserIds
                                            ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500/15 dark:border-rose-700"
                                            : "border-slate-200 focus:border-primary-500 focus:ring-primary-500/15 dark:border-slate-700"
                                    }`}
                                />
                                <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                                    Comma-separate user IDs. We will serialize them into the JSON string the backend expects.
                                </p>
                                {errors.targetUserIds ? (
                                    <p className="mt-1.5 text-sm text-rose-600 dark:text-rose-400">
                                        {errors.targetUserIds}
                                    </p>
                                ) : null}
                            </div>
                        ) : null}

                        <div>
                            <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                                Delivery Channels
                            </label>
                            <div className="flex flex-wrap gap-3">
                                <label className="flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-700 dark:border-slate-700 dark:text-slate-300">
                                    <input
                                        type="checkbox"
                                        checked={sendPush}
                                        onChange={(event) => {
                                            setSendPush(event.target.checked);
                                            clearFieldError("channels");
                                        }}
                                        className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                                    />
                                    Push
                                </label>
                                <label className="flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-700 dark:border-slate-700 dark:text-slate-300">
                                    <input
                                        type="checkbox"
                                        checked={sendEmail}
                                        onChange={(event) => {
                                            const nextChecked =
                                                event.target.checked;
                                            setSendEmail(nextChecked);
                                            if (!nextChecked) {
                                                setErrors(
                                                    (previousErrors) => ({
                                                        ...previousErrors,
                                                        emailTemplateFields:
                                                            undefined,
                                                    })
                                                );
                                            }
                                            clearFieldError("channels");
                                        }}
                                        className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                                    />
                                    Email
                                </label>
                            </div>
                            {errors.channels ? (
                                <p className="mt-1.5 text-sm text-rose-600 dark:text-rose-400">
                                    {errors.channels}
                                </p>
                            ) : null}
                        </div>

                        {mode === "schedule" ? (
                            <div>
                                <label className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">
                                    <CalendarDaysIcon className="h-4 w-4" /> Schedule For *
                                </label>
                                <input
                                    type="datetime-local"
                                    value={scheduledFor}
                                    onChange={(event) => {
                                        setScheduledFor(event.target.value);
                                        clearFieldError("scheduledFor");
                                    }}
                                    className={`w-full rounded-xl border px-4 py-3 text-sm text-slate-900 transition focus:outline-none focus:ring-4 dark:bg-slate-900 dark:text-slate-100 ${
                                        errors.scheduledFor
                                            ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500/15 dark:border-rose-700"
                                            : "border-slate-200 focus:border-primary-500 focus:ring-primary-500/15 dark:border-slate-700"
                                    }`}
                                />
                                {errors.scheduledFor ? (
                                    <p className="mt-1.5 text-sm text-rose-600 dark:text-rose-400">
                                        {errors.scheduledFor}
                                    </p>
                                ) : null}
                            </div>
                        ) : null}

                        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 pt-5 dark:border-slate-800">
                            <Button
                                variant="outline"
                                onClick={() => navigate("/notifications/announcements")}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handlePrimaryAction}
                                disabled={isSubmitting}
                            >
                                {mode === "send" ? (
                                    <>
                                        <PaperAirplaneIcon className="mr-1.5 h-4 w-4" />
                                        Review Approval Request
                                    </>
                                ) : mode === "schedule" ? (
                                    <>
                                        <ClockIcon className="mr-1.5 h-4 w-4" />
                                        Review Schedule Request
                                    </>
                                ) : (
                                    <>
                                        <DocumentTextIcon className="mr-1.5 h-4 w-4" />
                                        Review & Save Draft
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </section>

                <aside className="space-y-6 xl:sticky xl:top-24 xl:self-start">
                    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                        <div className="flex items-center gap-2">
                            <BellAlertIcon className="h-5 w-5 text-primary-500" />
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                                Delivery Preview
                            </h3>
                        </div>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                            Live previews update as the admin types so both push and email announcements can be reviewed before send.
                        </p>

                        {sendPush || sendEmail ? (
                            <div className="mt-5 space-y-5">
                                {sendPush ? (
                                    <div>
                                        <div className="mb-2 flex items-center gap-2">
                                            <BellAlertIcon className="h-4 w-4 text-primary-500" />
                                            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                                                Push Preview
                                            </p>
                                        </div>
                                        <div className="flex justify-center">
                                            <div className="w-full max-w-xs rounded-[30px] bg-slate-950 p-3 shadow-2xl">
                                                <div className="rounded-[24px] bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.32),transparent_36%),linear-gradient(180deg,#0f172a_0%,#111827_100%)] p-3">
                                                    <div className="rounded-2xl bg-white/95 p-4 text-slate-900 shadow-lg">
                                                        <div className="flex items-center justify-between gap-3">
                                                            <div className="flex items-center gap-2">
                                                                <div className="h-8 w-8 rounded-xl bg-primary-600/10" />
                                                                <div>
                                                                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                                                        RideToFive
                                                                    </p>
                                                                    <p className="text-[11px] text-slate-400">
                                                                        Push notification
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <span className="text-[11px] text-slate-400">
                                                                now
                                                            </span>
                                                        </div>
                                                        <div className="mt-4">
                                                            <p className="text-sm font-semibold text-slate-900">
                                                                {titlePreview}
                                                            </p>
                                                            <p className="mt-1 text-xs leading-5 text-slate-600">
                                                                {messagePreview}
                                                            </p>
                                                        </div>
                                                        {previewImageUrl ? (
                                                            <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
                                                                <img
                                                                    src={previewImageUrl}
                                                                    alt="Push announcement preview"
                                                                    className="h-28 w-full object-cover"
                                                                />
                                                            </div>
                                                        ) : null}
                                                        <div className="mt-4 flex flex-wrap gap-2">
                                                            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-600">
                                                                {fmtLabel(announcementType)}
                                                            </span>
                                                            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-600">
                                                                {fmtLabel(targetAudience)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : null}

                                {sendEmail ? (
                                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/40">
                                        <div className="flex items-center gap-2">
                                            <EnvelopeIcon className="h-4 w-4 text-emerald-500" />
                                            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                                                Email Preview
                                            </p>
                                        </div>
                                        <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700">
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: emailHtmlMarkup,
                                                }}
                                            />
                                        </div>
                                        <div className="mt-4 rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-900">
                                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                                Email Text Fallback
                                            </p>
                                            <p className="mt-2 whitespace-pre-wrap text-xs leading-5 text-slate-600 dark:text-slate-300">
                                                {emailTextFallback || "The email text fallback will appear here."}
                                            </p>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        ) : (
                            <div className="mt-5 rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-5 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-400">
                                Enable Push or Email to preview how this announcement will be delivered.
                            </div>
                        )}
                    </section>

                    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                            Delivery Summary
                        </h3>
                        <div className="mt-4 space-y-3 text-sm">
                            <div className="flex items-start justify-between gap-4">
                                <span className="text-slate-500 dark:text-slate-400">
                                    Action
                                </span>
                                <span className="font-medium text-slate-900 dark:text-slate-100">
                                    {selectedMode?.label}
                                </span>
                            </div>
                            <div className="flex items-start justify-between gap-4">
                                <span className="text-slate-500 dark:text-slate-400">
                                    Audience
                                </span>
                                <span className="text-right font-medium text-slate-900 dark:text-slate-100">
                                    {fmtLabel(targetAudience)}
                                </span>
                            </div>
                            <div className="flex items-start justify-between gap-4">
                                <span className="text-slate-500 dark:text-slate-400">
                                    Channels
                                </span>
                                <span className="text-right font-medium text-slate-900 dark:text-slate-100">
                                    {deliveryChannels.length > 0
                                        ? deliveryChannels.join(", ")
                                        : "None selected"}
                                </span>
                            </div>
                            <div className="flex items-start justify-between gap-4">
                                <span className="text-slate-500 dark:text-slate-400">
                                    Email Template
                                </span>
                                <span className="text-right font-medium text-slate-900 dark:text-slate-100">
                                    {selectedEmailTemplate.label}
                                </span>
                            </div>
                            <div className="flex items-start justify-between gap-4">
                                <span className="text-slate-500 dark:text-slate-400">
                                    Type
                                </span>
                                <span className="font-medium text-slate-900 dark:text-slate-100">
                                    {fmtLabel(announcementType)}
                                </span>
                            </div>
                            {mode === "schedule" ? (
                                <div className="flex items-start justify-between gap-4">
                                    <span className="text-slate-500 dark:text-slate-400">
                                        Scheduled For
                                    </span>
                                    <span className="text-right font-medium text-slate-900 dark:text-slate-100">
                                        {scheduledFor ? fmtDateTime(scheduledFor) : "Not set"}
                                    </span>
                                </div>
                            ) : null}
                            {targetAudience === "SPECIFIC_USERS" ? (
                                <div className="rounded-xl bg-slate-50 p-4 dark:bg-slate-800/40">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                        Selected User IDs
                                    </p>
                                    <p className="mt-2 break-all text-sm text-slate-700 dark:text-slate-200">
                                        {targetUserIds.length > 0
                                            ? targetUserIds.join(", ")
                                            : "No IDs entered yet"}
                                    </p>
                                </div>
                            ) : null}
                        </div>
                    </section>
                </aside>
            </div>

            <Transition appear show={confirmOpen} as={Fragment}>
                <Dialog
                    as="div"
                    className="relative z-50"
                    onClose={() => {
                        if (!isSubmitting) {
                            setConfirmOpen(false);
                        }
                    }}
                >
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto p-4">
                        <div className="flex min-h-full items-center justify-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-slate-900">
                                    <div className="border-b border-slate-200 px-6 py-4 dark:border-slate-800">
                                        <div className="flex items-start justify-between gap-4">
                                            <div>
                                                <Dialog.Title className="text-lg font-semibold text-slate-900 dark:text-white">
                                                    {confirmationCopy.title}
                                                </Dialog.Title>
                                                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                                    {confirmationCopy.description}
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setConfirmOpen(false)}
                                                className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                                            >
                                                <XMarkIcon className="h-5 w-5" />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="space-y-4 px-6 py-5">
                                        {mode === "send" ? (
                                            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
                                                <div className="flex items-start gap-3">
                                                    <ExclamationTriangleIcon className="mt-0.5 h-5 w-5" />
                                                    <div>
                                                        <p className="font-medium">
                                                            Approval required
                                                        </p>
                                                        <p className="mt-1">
                                                            Once confirmed here, the request enters the approval queue. Delivery only starts after approval.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : null}

                                        <div className="grid gap-3 rounded-2xl bg-slate-50 p-4 text-sm dark:bg-slate-800/40">
                                            <div className="flex justify-between gap-4">
                                                <span className="text-slate-500 dark:text-slate-400">
                                                    Title
                                                </span>
                                                <span className="text-right font-medium text-slate-900 dark:text-slate-100">
                                                    {titlePreview}
                                                </span>
                                            </div>
                                            <div className="flex justify-between gap-4">
                                                <span className="text-slate-500 dark:text-slate-400">
                                                    Type
                                                </span>
                                                <span className="text-right font-medium text-slate-900 dark:text-slate-100">
                                                    {fmtLabel(announcementType)}
                                                </span>
                                            </div>
                                            <div className="flex justify-between gap-4">
                                                <span className="text-slate-500 dark:text-slate-400">
                                                    Audience
                                                </span>
                                                <span className="text-right font-medium text-slate-900 dark:text-slate-100">
                                                    {fmtLabel(targetAudience)}
                                                </span>
                                            </div>
                                            <div className="flex justify-between gap-4">
                                                <span className="text-slate-500 dark:text-slate-400">
                                                    Channels
                                                </span>
                                                <span className="text-right font-medium text-slate-900 dark:text-slate-100">
                                                    {deliveryChannels.join(", ")}
                                                </span>
                                            </div>
                                            <div className="flex justify-between gap-4">
                                                <span className="text-slate-500 dark:text-slate-400">
                                                    Email Template
                                                </span>
                                                <span className="text-right font-medium text-slate-900 dark:text-slate-100">
                                                    {selectedEmailTemplate.label}
                                                </span>
                                            </div>
                                            <div className="flex justify-between gap-4">
                                                <span className="text-slate-500 dark:text-slate-400">
                                                    Image Source
                                                </span>
                                                <span className="text-right font-medium text-slate-900 dark:text-slate-100">
                                                    {imageSourceLabel}
                                                </span>
                                            </div>
                                            {mode === "schedule" ? (
                                                <div className="flex justify-between gap-4">
                                                    <span className="text-slate-500 dark:text-slate-400">
                                                        Scheduled For
                                                    </span>
                                                    <span className="text-right font-medium text-slate-900 dark:text-slate-100">
                                                        {fmtDateTime(scheduledFor)}
                                                    </span>
                                                </div>
                                            ) : null}
                                        </div>

                                        {sendPush ? (
                                            <div className="rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
                                                <div className="flex items-center gap-2">
                                                    <BellAlertIcon className="h-4 w-4 text-primary-500" />
                                                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                                                        Push Preview
                                                    </p>
                                                </div>
                                                <div className="mt-3 rounded-2xl bg-slate-50 p-4 dark:bg-slate-800/40">
                                                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                                        {titlePreview}
                                                    </p>
                                                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                                                        {messagePreview}
                                                    </p>
                                                    {previewImageUrl ? (
                                                        <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white">
                                                            <img
                                                                src={previewImageUrl}
                                                                alt="Push announcement preview"
                                                                className="h-28 w-full object-cover"
                                                            />
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </div>
                                        ) : null}
                                        {sendEmail ? (
                                            <div className="rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
                                                <div className="flex items-center gap-2">
                                                    <EnvelopeIcon className="h-4 w-4 text-emerald-500" />
                                                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                                                        Email Preview
                                                    </p>
                                                </div>
                                                <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700">
                                                    <div
                                                        dangerouslySetInnerHTML={{
                                                            __html: emailHtmlMarkup,
                                                        }}
                                                    />
                                                </div>
                                                <div className="mt-3 rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900">
                                                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                                        Email Text Fallback
                                                    </p>
                                                    <p className="mt-2 whitespace-pre-wrap text-xs leading-5 text-slate-600 dark:text-slate-300">
                                                        {emailTextFallback || "The email text fallback will appear here."}
                                                    </p>
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>

                                    <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4 dark:border-slate-800">
                                        <Button
                                            variant="outline"
                                            onClick={() => setConfirmOpen(false)}
                                            disabled={isSubmitting}
                                        >
                                            Go Back
                                        </Button>
                                        <Button
                                            onClick={handleConfirmSubmit}
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                "Processing..."
                                            ) : mode === "send" ? (
                                                <>
                                                    <PaperAirplaneIcon className="mr-1.5 h-4 w-4" />
                                                    {confirmationCopy.cta}
                                                </>
                                            ) : mode === "schedule" ? (
                                                <>
                                                    <ClockIcon className="mr-1.5 h-4 w-4" />
                                                    {confirmationCopy.cta}
                                                </>
                                            ) : (
                                                <>
                                                    <DocumentTextIcon className="mr-1.5 h-4 w-4" />
                                                    {confirmationCopy.cta}
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </div>
    );
};
